"use client";

import { motion } from "framer-motion";
import * as FaIcons from "react-icons/fa";
import * as MdIcons from "react-icons/md";
import * as AiIcons from "react-icons/ai";
import * as HiIcons from "react-icons/hi";
import * as IoIcons from "react-icons/io";
import * as Io2Icons from "react-icons/io5";
import * as BiIcons from "react-icons/bi";
import { useLocale } from "next-intl";
import { useGetSolutions } from "@/app/hooks/main-hooks";

export default function OurSolutions() {
  const { data } = useGetSolutions();
  const locale = useLocale();

  const allIcons = {
    ...FaIcons,
    ...MdIcons,
    ...AiIcons,
    ...HiIcons,
    ...IoIcons,
    ...Io2Icons,
    ...BiIcons,
  };

  return (
    <section
      id="solutions"
      className="relative py-24 bg-[#ffffff] overflow-hidden"
    >
      {/* Subtle background accents */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#d59726]/10 rounded-full blur-[140px]" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#d59726]/10 rounded-full blur-[140px]" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-[#d59726] font-semibold tracking-wide text-4xl md:text-6xl">
            {locale === "ar" ? "حلولنا" : "Our Solutions"}
          </span>

          <h2 className="font-black text-main-color mt-4 mb-6">
            {locale === "ar"
              ? "حلول ERP الذكية المبنية للنمو"
              : "Smart ERP Solutions Built for "}
            <span className="text-[#d59726]">
              {locale === "ar" ? "النمو" : "Growth"}
            </span>
          </h2>

          <p className="text-[#000000]/70 text-lg">
            {locale === "ar"
              ? "نقدم حلول ERP عملية وقابلة للتوسع تساعد المؤسسات على التحكم والوضوح والنجاح على المدى الطويل."
              : "We deliver practical, scalable ERP solutions that help organizations gain control, clarity, and long-term success."}
          </p>
        </motion.div>

        {/* Solutions Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {data?.solutions?.map((item: any, index: number) => {
            const Icon =
              allIcons[item?.icon as keyof typeof allIcons] ||
              FaIcons.FaRegQuestionCircle;

            return (
              <motion.div
                key={item.id || item?._id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group bg-[#ffffff] rounded-3xl p-8 border border-main-color/10 shadow-lg hover:shadow-2xl transition-all cursor-pointer"
              >
                {/* Icon */}
                <div className="w-16 h-16 rounded-xl bg-[#d59726]/15 flex items-center justify-center mb-6 transition group-hover:bg-[#d59726]/25">
                  <Icon size={28} className="text-[#d59726]" />
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-main-color mb-3 transition group-hover:text-[#d59726]">
                  {item?.name?.[locale]}
                </h3>

                {/* Description */}
                <p className="text-[#000000]/70 leading-relaxed">
                  {item?.description?.[locale]}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
